import axios from 'axios';
import { getCharacterPrompt } from '../data/ai/characterPrompts';

const OPENAI_API_KEY = process.env.NEXT_PUBLIC_OPENAI_API_KEY || '';
const POE_API_KEY = process.env.NEXT_PUBLIC_POE_API_KEY || '';

/**
 * 選擇 AI 提供商
 * 優先順序: OpenAI > POE > 模擬模式
 */
const AI_PROVIDER = (() => {
  if (OPENAI_API_KEY && OPENAI_API_KEY !== 'your_api_key_here') return 'openai';
  if (POE_API_KEY && POE_API_KEY !== 'your_poe_api_key_here') return 'poe';
  return 'mock';
})();

/**
 * 🆕 從對話歷史中提取使用者名字
 */
const extractUserName = (chatHistory) => {
  // 假設第一則系統訊息可能包含使用者名字
  // 或者從 localStorage/context 取得
  // 這裡先返回 null,實際應用中可以從 AuthContext 取得
  return null;
};

/**
 * 模擬回覆 (當沒有 API Key 時)
 */
const simulateResponse = (character, chatHistory, userName) => {
  return new Promise(resolve => {
    setTimeout(() => {
      const lastMsg = chatHistory[chatHistory.length - 1]?.content || "";
      
      if (lastMsg.includes("抽到了塔羅牌【")) {
        const greeting = userName 
          ? `${userName},燭光微微搖晃,占卜師凝視著你抽出的牌` 
          : `(燭光微微搖晃,占卜師凝視著你抽出的牌)`;
        
        resolve(`${greeting}\n\n這張牌出現得正是時候。它映照出了你內心深處那些尚未被承認的情緒。牌面的指引已經很清晰了,但我想問你:當你看到這張牌的第一眼,心裡浮現的是誰的影子?`);
      } else {
        const greeting = userName 
          ? `${userName},我聽到了。` 
          : `(${character.name}靜靜地看著你)`;
        
        resolve(`${greeting}\n\n你說「${lastMsg.substring(0, 15)}...」,這的確不容易。給自己一點時間去消化這些感受吧,我在這裡陪著你。`);
      }
    }, 2500);
  });
};

/**
 * 呼叫 OpenAI API
 */
const callOpenAI = async (character, chatHistory, userName) => {
  const promptConfig = getCharacterPrompt(character.id);
  
  // 🆕 如果有使用者名字,在 system prompt 加入
  let systemPrompt = promptConfig.systemPrompt;
  if (userName) {
    systemPrompt += `\n\n<使用者資訊>\n對方的名字是「${userName}」。請在對話中自然地稱呼對方的名字,增加親切感與代入感。\n</使用者資訊>`;
  }
  
  const formattedMessages = [
    { role: 'system', content: systemPrompt },
    ...chatHistory.map(msg => ({
      role: msg.role === 'ai' ? 'assistant' : 'user',
      content: msg.content
    }))
  ];

  const response = await axios.post(
    'https://api.openai.com/v1/chat/completions',
    {
      model: 'gpt-4o-mini',
      messages: formattedMessages,
      temperature: promptConfig.temperature,
      max_tokens: promptConfig.max_tokens,
      presence_penalty: 0.6,
      frequency_penalty: 0.3
    },
    {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${OPENAI_API_KEY}`
      }
    }
  );

  return response.data.choices[0].message.content;
};

/**
 * 呼叫 POE API
 * 注意: POE 免費版有每日請求限制 (通常 20-50 次)
 */
const callPOE = async (character, chatHistory, userName) => {
  const promptConfig = getCharacterPrompt(character.id);
  
  let systemPrompt = promptConfig.systemPrompt;
  if (userName) {
    systemPrompt += `\n\n對方的名字是「${userName}」。`;
  }

  const response = await axios.post(
    'https://api.poe.com/v1/chat/completions',
    {
      bot: 'GPT-4o-Mini',
      query: chatHistory[chatHistory.length - 1].content,
      context: systemPrompt,
      temperature: promptConfig.temperature
    },
    {
      headers: {
        'Authorization': `Bearer ${POE_API_KEY}`
      }
    }
  );

  return response.data.text;
};

export const chatService = {
  /**
   * 發送訊息並獲取 AI 回覆
   * 🆕 支援使用者名字
   */
  sendMessage: async (character, chatHistory, userName = null) => {
    try {
      switch (AI_PROVIDER) {
        case 'openai':
          return await callOpenAI(character, chatHistory, userName);
        case 'poe':
          return await callPOE(character, chatHistory, userName);
        default:
          return await simulateResponse(character, chatHistory, userName);
      }
    } catch (error) {
      console.error(`${AI_PROVIDER} API Error:`, error);
      return "(酒吧的燈光突然閃爍了一下,似乎網路連線遇到了一些問題。請稍後再試。)";
    }
  }
};